import { useEffect, useRef } from 'react';
import { Users, Languages } from 'lucide-react';
import { palette, admin, fontFamily, transition } from '../../../styles/tokens';
import { MessageBubble } from './MessageBubble';
import { Composer } from './Composer';
import type { GroupChat, Message } from '../types';

interface Props {
  group: GroupChat;
  messages: Message[];
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onSend: (content: string) => void;
  sending: boolean;
  showTranslations: boolean;
  onToggleTranslations: () => void;
}

export function GroupThreadPanel({
  group, messages, loading, hasMore, onLoadMore,
  onSend, sending, showTranslations, onToggleTranslations,
}: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const atBottomRef = useRef(true);

  useEffect(() => {
    if (atBottomRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [group.id]);

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    atBottomRef.current = scrollHeight - scrollTop - clientHeight < 40;
    if (scrollTop < 80 && hasMore && !loading) onLoadMore();
  };

  let lastSentIdx = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].isSent) { lastSentIdx = i; break; }
  }

  let lastDate = '';
  let lastSenderId = '';
  const elements: React.ReactNode[] = [];

  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    const date = new Date(msg.created_at).toLocaleDateString('en-CA', { month: 'long', day: 'numeric', year: 'numeric' });

    if (date !== lastDate) {
      lastDate = date;
      lastSenderId = '';
      elements.push(
        <div key={`date-${date}`} style={{ display: 'flex', justifyContent: 'center', padding: '16px 0 8px' }}>
          <span style={{
            background: palette.g100, borderRadius: 12, padding: '4px 16px',
            fontSize: 12, fontWeight: 500, color: palette.g500,
          }}>
            {date}
          </span>
        </div>,
      );
    }

    const isConsecutive = msg.sender_id === lastSenderId;
    lastSenderId = msg.sender_id;

    elements.push(
      <MessageBubble
        key={msg.id}
        message={msg}
        showTranslations={showTranslations}
        isLastSent={i === lastSentIdx}
        isConsecutive={isConsecutive}
        showSender
      />,
    );
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: palette.white, height: '100%', overflow: 'hidden' }}>
      {/* Group header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 16px', borderBottom: `1px solid ${admin.border}`,
        background: palette.white, flexShrink: 0,
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: 18, flexShrink: 0,
          background: palette.accentWash,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Users size={18} color={palette.accent} strokeWidth={1.9} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: palette.g800, fontFamily }}>
            {group.name}
          </div>
          <div style={{ fontSize: 11, color: palette.g500 }}>
            {group.memberCount} {group.memberCount === 1 ? 'member' : 'members'}
          </div>
        </div>

        {/* Translation toggle (parity with DM ThreadHeader) */}
        <button
          onClick={onToggleTranslations}
          title={showTranslations ? 'Show originals' : 'Show translations'}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            height: 32, borderRadius: 6, border: `1px solid ${admin.border}`,
            background: showTranslations ? palette.accentWash : palette.white,
            cursor: 'pointer', padding: '0 10px',
            transition: `background ${transition.fast}`,
          }}
        >
          <Languages size={14} color={showTranslations ? palette.accent : palette.g500} />
          {showTranslations && (
            <span style={{ fontSize: 11, fontWeight: 500, color: palette.accent }}>On</span>
          )}
        </button>
      </div>

      <div ref={scrollRef} onScroll={handleScroll} style={{ flex: 1, overflowY: 'auto', background: palette.bg }}>
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '8px 0' }}>
          {loading && messages.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: palette.g500 }}>Loading messages...</div>
          )}
          {!loading && messages.length === 0 && (
            <div style={{ padding: 40, textAlign: 'center', fontSize: 13, color: palette.g500 }}>No messages yet. Say hello!</div>
          )}
          {elements}
          <div style={{ height: 8 }} />
        </div>
      </div>

      <Composer onSend={onSend} sending={sending} />
    </div>
  );
}
